import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { StoryblokComponent, storyblokEditable } from '@storyblok/react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { shallowEqual, useDispatch } from 'react-redux';
import { useLocation } from 'react-router';

import { fetchStoryblokBlogPostStories } from '../../../../containers/StoryblokPage/StoryblokPage.duck';
import { neutral } from '../../../../styles/mui/colors';
import useAppSelector from '../../../../util/hooks/useAppSelector';
import { logError } from '../../../../util/log';
import { getStoryblokClient } from '../../../../util/storyblok';
import { withBaseComponent } from '../../hoc/withBaseComponent';
import StoryblokArchiveBlogCategory from './StoryblokArchiveBlogCategory';
import StoryblokArchiveBlogPagination from './StoryblokArchiveBlogPagination';
import StoryblokArchivePostTile, { StoryblokArchiveSkeletonPostTile } from './StoryblokArchivePostTile';

const storyblokArchiveBlogCategoryBoxStyles = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  flexWrap: 'wrap',
  gap: 2,
  mb: 2,
  width: '100%',
  bgcolor: 'white',
  border: `1px solid ${neutral[200]}`,
  borderRadius: 200,
  p: 1,
};

export const fetchPostsDirectlyByUUIDs = async (uuids, locale) => {
  const storyblokApi = getStoryblokClient();
  try {
    return await Promise.all(
      uuids.map((uuid) =>
        storyblokApi
          .get(`cdn/stories/${uuid}`, {
            find_by: 'uuid',
            version: 'published', // Change to 'draft' if needed
            language: locale === 'de' ? 'default' : locale,
            resolve_relations: 'blog_post.categories',
          })
          .then((response) => response.data.story),
      ),
    ); // This will be an array of stories
  } catch (error) {
    logError(error, 'story-blok-fetch-posts-by-uuids');
    throw error;
  }
};

/**
 * @param {object} props
 * @param {object} props.blok
 */

const StoryblokArchiveBlog = ({ blok }) => {
  const { general = { showAll: true }, extra = { id: '' } } = blok;
  const { id } = extra;
  const { showAll } = general;
  const { posts } = general;
  const dispatch = useDispatch();
  const location = useLocation();
  const intl = useIntl();

  // Fixing the issue of same state different posts if two ARchives on one page
  const [localPosts, setLocalPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (posts && posts.length > 0) {
      setIsLoading(true);
      fetchPostsDirectlyByUUIDs(posts, intl.locale)
        .then((fetchedPosts) => {
          setLocalPosts(fetchedPosts);
          setIsLoading(false);
        })
        .catch((err) => {
          setError('Failed to load posts');
          setIsLoading(false);
          logError(err, 'story-blok-fetch-posts-by-uuids');
        });
    } else {
      setLocalPosts([]);
    }
  }, [posts, intl.locale]);

  const { blogPostStories, blogPostStoriesInProgress } = useAppSelector(
    (state) => ({
      blogPostStories: state.StoryblokPage.blogPostStories,
      blogPostStoriesInProgress: state.StoryblokPage.blogPostStoriesInProgress,
    }),
    shallowEqual,
  );
  const blogPostCategories = useAppSelector(
    useCallback((state) => Object.values(state.marketplaceData.storyblokEntities.category), []),
  );

  const onFetchBlogPostStories = useCallback(
    (category) => dispatch(fetchStoryblokBlogPostStories(category, 12, intl.locale)),
    [dispatch, intl.locale],
  );

  const search = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [currentCategory, setCurrentCategory] = useState(search.get('category') || '');
  const [currentPage, setCurrentPage] = useState(search.get('page') || 1);

  const firstUpdate = useRef(true);
  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    const category = search.get('category') || null;
    const page = search.get('page') || 1;
    setCurrentCategory(category);
    setCurrentPage(page);
    onFetchBlogPostStories(search);
  }, [location.search, onFetchBlogPostStories, search]);

  const categoriesElements = (
    <Box sx={storyblokArchiveBlogCategoryBoxStyles}>
      {showAll ? (
        <StoryblokArchiveBlogCategory
          blok={{
            label: intl.formatMessage({ id: 'StoryblokArchiveBlog.showAll' }),
          }}
          category={null}
        />
      ) : null}
      {blogPostCategories.map((category) => {
        if (typeof category === 'object') {
          return (
            <StoryblokComponent
              blok={category.content}
              category={category.slug}
              currentCategory={currentCategory}
              key={category.content._uid}
            />
          );
        } else {
          return null;
        }
      })}
    </Box>
  );

  const blogPostSkeletons = blogPostStoriesInProgress && blogPostStories.length <= 0 ? (
    <Grid container spacing={2}>
      {[...Array(12).keys()].map((key) => (
        <Grid key={key} size={{ md: 4, sm: 6, xs: 12 }}>
          <StoryblokArchiveSkeletonPostTile />
        </Grid>
      ))}
    </Grid>
  ) : null;

  const blogPostTiles = blogPostStories.length > 0 ? (
    <Grid container spacing={2}>
      {blogPostStories.map((story) => (
        <Grid key={story.uuid} size={{ md: 4, sm: 6, xs: 12 }}>
          <StoryblokArchivePostTile
            content={story.content}
            heroImage={story.content.heroImage}
            key={story.content._uid}
            metatags={story.content.metatags}
            publishedAt={story.first_published_at || story.published_at}
            slug={story.slug}
          />
        </Grid>
      ))}
    </Grid>
  ) : null;

  const noBlogPostsTiles = blogPostStories.length === 0 ? (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      {intl.formatMessage({ id: 'StoryblokArchiveBlog.noPosts' })}
    </Box>
  ) : null;

  const paginationElement = blogPostStories.length > 0 ? (
    <StoryblokArchiveBlogPagination currentCategory={currentCategory} currentPage={currentPage} />
  ) : null;

  const renderPosts = () => {
    if (isLoading) return <p>Loading posts...</p>;
    if (error) return <p>Error loading posts: {error}</p>;

    return (
      <Grid container spacing={2}>
        {localPosts.map((post) => (
          <Grid key={post.uuid} size={{ md: 4, sm: 6, xs: 12 }}>
            <StoryblokArchivePostTile
              content={post.content}
              heroImage={post.content.heroImage}
              key={post.uuid}
              metatags={post.content.metatags}
              publishedAt={post.first_published_at || post.published_at}
              slug={post.slug}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  return !posts ? (
    <Box id={id} {...storyblokEditable(blok)} width="100%">
      {categoriesElements}
      {blogPostSkeletons}
      {blogPostTiles}
      {noBlogPostsTiles}
      {paginationElement}
    </Box>
  ) : (
    <Box id={id} {...storyblokEditable(blok)} width="100%">
      {renderPosts()}
    </Box>
  );
};

export default withBaseComponent(StoryblokArchiveBlog);
